import { Method } from 'axios';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@tools/ui/api';
import { getShadowModeParams } from '../utils/shadow-mode';

enum RequestState {
  Loading = 'loading',
  Error = 'error'
}

const responseCache = new Map<string, unknown>();

export function useApiRequest<ResponseType>(options: {
  method: Method;
  path: string;
  queryParams?: object;
  body?: object;
  skip?: boolean;
  cacheKey?: string;
}) {
  const [refetchCounter, setRefetchCounter] = useState(0);
  const params = useRef<object | undefined>(undefined);
  const body = useRef<object | undefined>(undefined);
  const [state, setState] = useState<RequestState | ResponseType>(
    () =>
      (responseCache.get(options?.cacheKey || '') as
        | ResponseType
        | undefined) ?? RequestState.Loading
  );

  params.current = isEqual(params.current, options.queryParams)
    ? params.current
    : options.queryParams;

  body.current = isEqual(body.current, options.body)
    ? body.current
    : options.body;

  const refetch = useCallback(() => {
    if (options.cacheKey) {
      responseCache.delete(options.cacheKey);
    }
    setRefetchCounter((currentValue) => currentValue + 1);
  }, [options.cacheKey]);

  const fetchData = useCallback(
    async (signal: AbortSignal) => {
      setState(RequestState.Loading);

      try {
        const response = await api.request({
          method: options.method,
          url: options.path,
          params: { ...params.current, ...getShadowModeParams() },
          data: body.current,
          signal
        });

        if (options.cacheKey) {
          responseCache.set(options.cacheKey, response);
        }

        setState(response as ResponseType);
      } catch (error) {
        const wasCanceled =
          (error as { message?: string } | undefined)?.message === 'canceled';

        setState(wasCanceled ? RequestState.Loading : RequestState.Error);
      }
    },
    [options.method, options.path, options.cacheKey]
  );

  useEffect(() => {
    const controller = new AbortController();

    if (!options.skip) {
      const cached = responseCache.get(options?.cacheKey || '') as
        | ResponseType
        | undefined;

      if (cached) {
        setState(cached);
      } else {
        void fetchData(controller.signal);
      }
    }

    return () => {
      controller.abort();
    };
  }, [
    options.method,
    options.path,
    params.current,
    body.current,
    options.skip,
    options.cacheKey,
    refetchCounter,
    fetchData
  ]);

  return {
    loading: state === RequestState.Loading,
    error: state === RequestState.Error,
    data:
      state === RequestState.Loading || state === RequestState.Error
        ? undefined
        : state,
    refetch
  };
}
